import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';

import * as adminApi from '../../api/admin';
import type { LogFilters, SystemLog } from '../../api/admin';
import { SortHeader } from '../../components/SortHeader';
import { SearchableSelect } from '../../components/SearchableSelect';
import { useTableSort, sortRows } from '../../util/tableSort';
import { useTenantNames } from '../../util/useTenantNames';

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 200];

type LogSortKey =
  | 'created_at'
  | 'pid'
  | 'level'
  | 'logger_name'
  | 'tenant_id'
  | 'user'
  | 'message';

/**
 * System log viewer — superadmin only (route is role-gated in App.tsx).
 *
 * The backend appends to `public.system_logs` from a stdlib-logging
 * handler. Every record carries datetime, pid, level, the user +
 * tenant attributed via ContextVar in the auth dependency, the
 * logger name, and the message. We render them newest-first in a
 * sticky-header table with light filters.
 *
 * Auto-refresh polls every 5s when enabled. Each poll only asks for
 * logs newer than the previously-most-recent `created_at`, so we
 * don't re-pull and re-render the whole page on every tick.
 *
 * For an MVP the table renders directly; if the row count climbs
 * past a few hundred we'd want virtualisation (react-window).
 */

// Severity order, least to most — the dropdown keeps it (keepOrder)
// rather than alphabetizing; "(all)" is pinned on top.
const LEVELS = ['DEBUG', 'INFO', 'WARNING', 'ERROR', 'CRITICAL'];
const LEVEL_OPTIONS = LEVELS.map((l) => ({ value: l, label: l }));
const LEVEL_PINNED = [{ value: '', label: '(all)' }];
const POLL_MS = 5_000;

function levelColor(
  level: string,
): 'default' | 'info' | 'warning' | 'error' | 'success' {
  switch (level) {
    case 'WARNING':
      return 'warning';
    case 'ERROR':
    case 'CRITICAL':
      return 'error';
    case 'INFO':
      return 'info';
    case 'DEBUG':
      return 'default';
    default:
      return 'default';
  }
}

function formatTs(iso: string): string {
  // YYYY-MM-DD HH:MM:SS in the user's local time — matches what
  // they'd see if they tail-ed the container logs.
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

function shortId(uuid: string | null): string {
  return uuid ? uuid.slice(0, 8) : '—';
}

export function SystemLogs({
  embedded = false,
  actionsSlot = null,
}: { embedded?: boolean; actionsSlot?: HTMLElement | null } = {}) {
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [total, setTotal] = useState(0);
  const [level, setLevel] = useState('');
  const [search, setSearch] = useState('');
  // The search text only becomes a query on Apply / Enter — `appliedSearch`
  // is what the fetch actually uses, so typing doesn't hammer the API.
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  // Newest-first by default; every column is click-to-sort (within the
  // current page).
  const sort = useTableSort<LogSortKey>('created_at', 'desc');
  const tenantName = useTenantNames();

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: LogFilters = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      if (level) filters.level = level;
      if (appliedSearch.trim()) filters.search = appliedSearch.trim();
      const res = await adminApi.listLogs(filters);
      setLogs(res.items);
      setTotal(res.total);
    } catch (e: unknown) {
      setError(
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'failed to load logs',
      );
    } finally {
      setLoading(false);
    }
  }, [level, appliedSearch, page, rowsPerPage]);

  // Load on mount + whenever the page, page size, level, or applied
  // search changes.
  useEffect(() => {
    void fetchPage();
  }, [fetchPage]);

  // Auto-refresh only the newest page — polling a deep offset page every
  // few seconds would be wasteful and jumpy as new rows shift the window.
  useEffect(() => {
    if (!autoRefresh || page !== 0) return;
    const id = window.setInterval(() => {
      void fetchPage();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [autoRefresh, page, fetchPage]);

  // Apply the level filter immediately (dropdown) — reset to the first
  // page since the result set changed.
  const changeLevel = (next: string) => {
    setLevel(next);
    setPage(0);
  };

  // Apply / Enter commits the search box and jumps back to page 0.
  const applySearch = () => {
    setAppliedSearch(search);
    setPage(0);
  };

  const controls = (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
      <SearchableSelect
        id="log-level"
        label="Level"
        value={level}
        onChange={changeLevel}
        options={LEVEL_OPTIONS}
        pinned={LEVEL_PINNED}
        keepOrder
        sx={{ minWidth: 130 }}
      />
      <TextField
        size="small"
        label="Search message"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={(e) => {
          // Enter triggers reload — typing alone doesn't, to avoid
          // hammering the API on every keystroke.
          if (e.key === 'Enter') {
            applySearch();
          }
        }}
        sx={{ width: 240 }}
      />
      <Button variant="outlined" onClick={applySearch}>
        Apply
      </Button>
      <Tooltip title="Refresh now">
        <span>
          <IconButton onClick={() => void fetchPage()} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </span>
      </Tooltip>
      <FormControlLabel
        control={
          <Switch
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
        }
        label={`Auto-refresh ${POLL_MS / 1000}s`}
      />
    </Stack>
  );

  return (
    <Stack spacing={2}>
      {actionsSlot ? (
        createPortal(controls, actionsSlot)
      ) : (
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
          {embedded ? (
            <Box sx={{ flexGrow: 1 }} />
          ) : (
            <Typography variant="h4" sx={{ flexGrow: 1 }}>
              System logs
            </Typography>
          )}
          {controls}
        </Stack>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      <Typography variant="caption" color="text.secondary">
        {total.toLocaleString()} log entries match the current filters
      </Typography>

      <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <SortHeader
                label="Time"
                col="created_at"
                sort={sort}
                sx={{ minWidth: 160 }}
              />
              <SortHeader label="PID" col="pid" sort={sort} sx={{ width: 60 }} />
              <SortHeader
                label="Level"
                col="level"
                sort={sort}
                sx={{ width: 100 }}
              />
              <SortHeader
                label="Logger"
                col="logger_name"
                sort={sort}
                sx={{ minWidth: 180 }}
              />
              <SortHeader
                label="Tenant"
                col="tenant_id"
                sort={sort}
                sx={{ minWidth: 140 }}
              />
              <SortHeader
                label="User"
                col="user"
                sort={sort}
                sx={{ width: 100 }}
              />
              <SortHeader label="Message" col="message" sort={sort} />
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No log entries yet. Tail will populate as the app emits records.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {sortRows(
              logs,
              (row) => {
                switch (sort.orderBy) {
                  case 'created_at':
                    return row.created_at;
                  case 'pid':
                    return row.pid;
                  case 'level':
                    return row.level;
                  case 'logger_name':
                    return row.logger_name;
                  case 'tenant_id':
                    return tenantName(row.tenant_id) ?? row.tenant_id;
                  case 'user':
                    return row.user_name ?? row.user_id;
                  case 'message':
                    return row.message;
                }
              },
              sort.order,
            ).map((row) => (
              <TableRow key={row.id} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {formatTs(row.created_at)}
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {row.pid}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.level}
                    color={levelColor(row.level)}
                    variant={row.level === 'INFO' ? 'outlined' : 'filled'}
                  />
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {row.logger_name}
                </TableCell>
                <TableCell sx={{ fontSize: '0.85rem' }}>
                  <Tooltip title={row.tenant_id ?? ''}>
                    <span>
                      {tenantName(row.tenant_id) ?? shortId(row.tenant_id)}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ fontSize: '0.85rem' }}>
                  <Tooltip title={row.user_id ?? ''}>
                    <span>
                      {row.user_name ??
                        (row.user_id ? shortId(row.user_id) : '—')}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {row.message}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_e, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      />
    </Stack>
  );
}
